import React, { createElement, Component } from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { times } from 'ramda';
import moment from 'moment';
import { injectIntl } from 'react-intl';

import { Paper } from 'material-ui';
import ArrowBack from 'material-ui/svg-icons/hardware/keyboard-arrow-left';
import ArrowNext from 'material-ui/svg-icons/hardware/keyboard-arrow-right';

import ReactDatePicker, { CalendarContainer } from 'react-datepicker';
import { formatDate } from 'lib/dateDisplay';

import './react-datepicker-cssmodules.less';

import { view, editBox, headerYear, headerMonth, headerRow, headerArrow } from './Datepicker.less';

const defaultMinDate = moment().subtract(100, 'years');
const defaultMaxDate = moment();

const getMinDate = (values, hook, def) =>
  hook && values[hook] && moment(values[hook]).isValid() ? moment(values[hook]) : def;

const yearElements = (min, max) =>
  times(
    n => {
      const y = moment(max).year() - n;
      return (
        <option style={{ color: 'black' }} key={`year__${y}`} value={y}>
          {y}
        </option>
      );
    },
    moment(max).year() - moment(min).year() + 1,
  );

const monthElements = (min, max, date) =>
  moment.months().map((m, i) => {
    if (moment([date.year(), i]).endOf('month') >= moment(min) && moment([date.year(), i]) <= moment(max))
      return (
        <option style={{ color: 'black' }} key={`month__${m}`} value={i}>
          {m}
        </option>
      );
    // Return gray option if month outside of scope.
    // This is needed for navigating if year is changed through year dropdown
    else
      return (
        <option style={{ color: 'silver' }} key={`month__${m}`} value={i}>
          {m}
        </option>
      );
  });

const PaperContainer = ({ children, className }) => (
  <Paper style={{ padding: '0rem', margin: '0rem' }}>
    <CalendarContainer className={className}>{children}</CalendarContainer>
  </Paper>
);

PaperContainer.propTypes = {
  children: PropTypes.array,
  className: PropTypes.string,
};

class DatePickerInput extends Component {
  render() {
    const { onClick, value, placeholder } = this.props;
    return <input type="text" readOnly className={editBox} onClick={onClick} value={value} placeholder={placeholder} />;
  }
}

DatePickerInput.propTypes = {
  onClick: PropTypes.func,
  value: PropTypes.string,
  placeholder: PropTypes.string,
};

const CustomHeader = (minDate, maxDate) => {
  const header = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Grid fluid>
      <Row middle="xs" between="xs" className={headerRow}>
        <Col xs={2} className={headerArrow} onClick={e => !prevMonthButtonDisabled && decreaseMonth(e)}>
          {!prevMonthButtonDisabled && <ArrowBack style={{ width: '3.2rem', height: '3.2rem' }} color="#ffffff" />}
        </Col>
        <Col xs={8}>
          <select className={headerYear} value={moment(date).year()} onChange={e => changeYear(e.target.value)}>
            {yearElements(minDate, maxDate)}
          </select>
          <select className={headerMonth} value={moment(date).month()} onChange={e => changeMonth(e.target.value)}>
            {monthElements(minDate, maxDate, date)}
          </select>
        </Col>
        <Col xs={2} className={headerArrow} onClick={e => !nextMonthButtonDisabled && increaseMonth(e)}>
          {!nextMonthButtonDisabled && <ArrowNext style={{ width: '3.2rem', height: '3.2rem' }} color="#ffffff" />}
        </Col>
      </Row>
    </Grid>
  );

  header.propTypes = {
    date: PropTypes.instanceOf(moment),
    changeYear: PropTypes.func,
    changeMonth: PropTypes.func,
    decreaseMonth: PropTypes.func,
    increaseMonth: PropTypes.func,
    prevMonthButtonDisabled: PropTypes.bool,
    nextMonthButtonDisabled: PropTypes.bool,
  };

  return header;
};

const Datepicker = ({
  editing,
  input,
  meta,
  localeMessage,
  hintLocale,
  formValues,
  dateFloorHook,
  intl: { locale, formatMessage },
  maxDate = null,
  ...other
}) =>
  editing
    ? createElement(ReactDatePicker, {
        locale,
        name: input.name,
        onChange: input.onChange,
        selected: moment(input.value).isValid() ? moment(input.value) : undefined,
        minDate: getMinDate(formValues, dateFloorHook, defaultMinDate),
        maxDate: maxDate || defaultMaxDate,
        placeholderText: hintLocale ? localeMessage(hintLocale) : formatMessage({ id: 'datepicker.chooseDate' }),
        withPortal: true,
        fixedHeight: true,
        calendarContainer: PaperContainer,
        customInput: createElement(DatePickerInput),
        renderCustomHeader: CustomHeader(
          getMinDate(formValues, dateFloorHook, defaultMinDate),
          maxDate || defaultMaxDate,
        ),
        autoComplete: 'off',
        ...other,
      })
    : createElement('div', { className: view }, formatDate(input.value));

export default injectIntl(Datepicker);
